import type {
  GetMbcCatalogResponse,
  ListMbcAdminAssignmentsResponse,
} from "@nlc/shared-types";

import { Link } from "@/components/link";

interface UsedByLink {
  readonly href: string;
  readonly id: number;
  readonly name: string;
}

interface GuidelineUsedByProps {
  readonly assignments: ListMbcAdminAssignmentsResponse;
  readonly catalog: GetMbcCatalogResponse;
  readonly guidelineId: string;
}

export function GuidelineUsedBy({
  assignments,
  catalog,
  guidelineId,
}: GuidelineUsedByProps) {
  const subCategoryNames = new Map(
    catalog.subCategories.map(({ id, name }) => [id, name]),
  );
  const designTypeNames = new Map(
    [
      ...catalog.subCategories.flatMap(({ designTypes }) => designTypes),
      ...catalog.uncategorizedDesignTypes,
    ].map(({ id, name }) => [id, name]),
  );

  const subCategories: UsedByLink[] = assignments.subCategories
    .filter((assignment) => assignment.guidelineId === guidelineId)
    .map(({ mysqlId }) => ({
      href: `/admin/subcategories/${mysqlId}`,
      id: mysqlId!,
      name: subCategoryNames.get(mysqlId!) ?? `#${mysqlId}`,
    }));
  const designTypes: UsedByLink[] = assignments.designTypes
    .filter((assignment) => assignment.guidelineId === guidelineId)
    .map(({ mysqlId }) => ({
      href: `/admin/design-types/${mysqlId}`,
      id: mysqlId!,
      name: designTypeNames.get(mysqlId!) ?? `#${mysqlId}`,
    }));
  const isDefault = assignments.default.guidelineId === guidelineId;
  const isAssigned = isDefault || subCategories.length + designTypes.length > 0;

  return (
    <section className="p-6">
      <h2 className="text-md font-semibold text-text-primary">Used by</h2>
      <p className="mb-md text-sm text-text-tertiary">
        Where this guideline is assigned directly.
        {isDefault &&
          " It is the default guideline, so it also applies to everything with no guideline assigned."}
      </p>
      {isAssigned ? (
        <div className="flex flex-col gap-sm text-sm">
          {isDefault && (
            <span className="text-text-tertiary">Default guideline</span>
          )}
          <LinkList label="Subcategories" links={subCategories} />
          <LinkList label="Design types" links={designTypes} />
        </div>
      ) : (
        <p className="text-sm text-text-tertiary">
          Not assigned to any subcategory or design type.
        </p>
      )}
    </section>
  );
}

function LinkList({
  label,
  links,
}: {
  label: string;
  links: readonly UsedByLink[];
}) {
  if (links.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-md gap-y-xs">
      <span className="text-text-tertiary">{label}:</span>
      {links.map(({ href, id, name }) => (
        <Link className="font-medium" href={href} key={id}>
          {name}
        </Link>
      ))}
    </div>
  );
}
